
import {
  computed,
  defineComponent, onMounted, ref, Ref, watch, onUnmounted, PropType,
} from 'vue';
import { useDropZone } from '@vueuse/core';
import { useRoute } from 'vue-router';
import RefreshPull from 'pull-refresh-vue3';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useDriveStore } from '../store/driveStore';
import ItemPhoto from '../components/items/ItemPhoto.vue';
import ItemVideo from '../components/items/ItemVideo.vue';
import { MEDIA_TYPES, PHOTO_VIEW_LAYOUT, UPLOAD_PLACES } from '@/types/enums';
import AppSkeletonMedia from '@/components/skeletons/AppSkeletonMedia.vue';
import AppSkeletonRect from '@/components/skeletons/AppSkeletonRect.vue';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import SkeletonService from '@/services/SkeletonService';
import UsePagination from '@/composables/UsePagination';
import { DIRECTION } from '@/helpers/isScrollEnd';
import {
  IData, IList, IMedia, IMixedData, IResponse, ITemporaryFile,
} from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import DriveService from '../services/DriveService';
// eslint-disable-next-line import/no-cycle
import MediaService from '@/services/MediaService';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
// eslint-disable-next-line import/no-cycle
import UploadService from '@/services/UploadService';
import UseUpload from '@/composables/UseUpload';
import AppNoMessage from '@/components/AppNoMessage.vue';
import AppHeaderUploadProgress from '@/components/AppHeaderUploadProgress.vue';
import { convertBmpInFileList } from '@/helpers/bmpConvertor';
import AppDropZone from '@/components/AppDropZone.vue';

export default defineComponent({
  props: {
    selector: { type: Boolean, default: false },
    hideFooter: { type: Boolean, default: false },
    temporaryFiles: { type: Array as PropType<ITemporaryFile[]> },
  },
  components: {
    ItemPhoto,
    ItemVideo,
    AppSkeletonMedia,
    AppSkeletonRect,
    RefreshPull,
    AppNoMessage,
    AppHeaderUploadProgress,
    AppDropZone,
  },
  setup(props) {
    const route = useRoute();
    const mainStore = useMainStore();
    const driveStore = useDriveStore();

    const { onUploadUpdate } = UseUpload();

    const { photoSkeletons } = SkeletonService.getSkeletonInfo();

    const dataLoading = ref(false);
    const column = computed(() => driveStore.photoViewLayout === PHOTO_VIEW_LAYOUT.LIST);

    const isNetwork = computed(() => route.meta.isNetwork);

    const photosLastPage = computed(() => (isNetwork.value ? driveStore.networkPhotos.lastPage : driveStore.photos.lastPage));
    const photosCurrentPage = computed(() => (isNetwork.value ? driveStore.networkPhotos.page : driveStore.photos.page));

    const photos = computed(() => {
      if (driveStore.search) {
        return driveStore.searchPhotos;
      }
      return (isNetwork.value ? driveStore.networkPhotos.data : driveStore.photos.data);
    });

    const noPhotosMessage = computed(() => {
      if (!dataLoading.value && !photos.value.length) {
        if (driveStore.search) {
          return {
            text: 'No search results found',
            icon: 'icon-not-found',
          };
        }
        return {
          text: isNetwork.value ? 'Nothing shared with you yet...' : 'No photos here yet...',
          icon: 'icon-no-data',
        };
      }
      return '';
    });

    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage, onCacheScroll,
    } = UsePagination();

    function setSelectedStatus() {
      if (!props.selector) {
        return;
      }

      driveStore.photos.data.forEach((photo) => {
        if (props.temporaryFiles) {
          // eslint-disable-next-line no-param-reassign
          photo.selected = !!props.temporaryFiles!.find((current) => current.id === photo.id);
        } else {
          // eslint-disable-next-line no-param-reassign
          photo.selected = !!driveStore.currentCase.photos.find((current) => current.contentId === photo.contentId);
        }
      });
    }

    function setSearchSelectedStatus() {
      if (!props.selector) {
        return;
      }

      driveStore.searchPhotos.forEach((photo) => {
        // eslint-disable-next-line no-param-reassign
        photo.selected = !!driveStore.currentCase.photos.find((current) => current.contentId === photo.contentId);
      });
    }

    async function fetchCacheData() {
      const resolveRequest = () => {
        if (isNetwork.value) {
          return driveStore.fetchNetworkPhotos();
        }
        return driveStore.fetchPhotos();
      };

      return resolveRequest()
        .then((res) => {
          setSelectedStatus();
          driveStore.photoList = photos.value;
          return res;
        });
    }

    async function fetchSearchData() {
      const currentSearch = driveStore.search;
      const resolveRequest = async () => {
        if (isNetwork.value) {
          return DriveService.getNetworkPhotos(pageIdx.value, driveStore.search)
            .then((res) => {
              const photosResponse: any = { ...res };
              photosResponse.results.data.items = photosResponse.results.data.items.map((item: IMixedData) => ({ ...item.file as IMedia, sharedTime: item.shared }));
              return photosResponse as IResponse<IData<IList<IMedia>>>;
            });
        }
        return MediaService.getMediaByType([MEDIA_TYPES.PHOTO, MEDIA_TYPES.VIDEO], pageIdx.value, driveStore.search);
      };

      return resolveRequest()
        .then((response) => {
          if (currentSearch !== driveStore.search) {
            return Promise.reject(new Error('response is out of date'));
          }
          if (pageIdx.value === 0) {
            driveStore.searchPhotos = response.results.data.items;
          } else {
            driveStore.searchPhotos.push(...response.results.data.items);
          }
          setSearchSelectedStatus();
          driveStore.photoList = photos.value;
          return response;
        });
    }

    function handleScroll() {
      if (driveStore.search) {
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, photosLastPage.value, photosCurrentPage.value);
      }
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 1000);
    });

    function deselectAll(items: IMedia[]): IMedia[] {
      return items.map((item) => ({
        ...item,
        selected: false,
      }));
    }

    watch(() => driveStore.isPhotoSelector, (newVal, prevVal) => {
      if (prevVal && !newVal) {
        driveStore.photos.data = deselectAll(driveStore.photos.data);
        driveStore.searchPhotos = deselectAll(driveStore.searchPhotos);
      }
    });

    function refreshPhotos() {
      if (isNetwork.value) {
        driveStore.networkPhotos.page = 0;
        driveStore.networkPhotos.lastPage = -1;
      } else {
        driveStore.photos.page = 0;
        driveStore.photos.lastPage = -1;
      }

      return fetchCacheData();
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshPhotos);

    async function onDrop(fileList: File[] | null) {
      if (fileList) {
        const newFileList = await convertBmpInFileList(fileList as unknown as FileList);
        const fileUploads = UploadService.toFileUploads(newFileList);
        UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.PHOTO });
      }
    }
    const dropZoneRef = ref<HTMLElement>();

    const { isOverDropZone } = useDropZone(dropZoneRef, onDrop);

    onMounted(async () => {
      MonitoringService.sendPageMountedSpeed();
      driveStore.search = '';
      setSelectedStatus();
      // driveStore.photos.page = 1;
      // await driveStore.fetchPhotos();
      // driveStore.photos.page = 2;
      // await driveStore.fetchPhotos();
    });

    onUnmounted(() => {
      driveStore.photos.data = deselectAll(driveStore.photos.data);
      driveStore.searchPhotos = deselectAll(driveStore.searchPhotos);
    });

    return {
      mainStore,
      driveStore,
      dataLoading,
      photos,
      column,
      scroller,
      isNetwork,
      isPageLoading,
      handleScroll,
      MEDIA_TYPES,
      PHOTO_VIEW_LAYOUT,
      photoSkeletons,
      noPhotosMessage,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,
      dropZoneRef,
      isOverDropZone,
    };
  },
});
